import PathConstants from '@/main/routes/paths'
import { Box, Container, Tabs, useTheme } from '@mui/material'
import { Link } from 'react-router-dom'

import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'
import { Account } from '@/domain/usecases'
import { ValidAccountState } from '@/presentation/contexts/auth'
import useAccount from '@/presentation/hooks/auth/use-account'
import { TabApp } from '../../../navigation/tab/tab-app'
import { TabMenuApp } from '../../../navigation/tab/tab-menu-app'

import HomeIcon from '@mui/icons-material/Home'
import { FeatureFlagName, useFeatureFlags } from '@/infra/feature-flag'
import ProfileAssessmentButton from '../../../button/profile-assessment-button'
import { AnalyticsEventName, AnalyticsTracker } from '@/infra/tracking'
import { useActiveTab } from '../shared/use-active-tab'

// Desktop top bar (tabs). Counterpart for dropdown/mobile: nav-config.ts → buildProfileSection()
// When adding/removing items here, mirror the change in buildProfileSection.
export default function DesktopNavMenuMain() {
  const theme = useTheme()
  const [account] = useAccount() as ValidAccountState
  const roles = Account.getRoles(account)
  const routes = PathConstants.Routes
  const { HOME, INSTITUTIONS } = routes
  const restrictions = account.company.contentRestrictions.categories
  const enrollmentCategoryList = EnrollmentCategory.getAvailables(restrictions)

  const { isProfileAssessmentEnabled } = useFeatureFlags({
    isProfileAssessmentEnabled: FeatureFlagName.COMPANIES_WITH_PROFILE_ASSESSMENT
  })

  const findSelectedEnrollmentCategory = (pathname: string) => {
    return enrollmentCategoryList.find(ec =>
      ec
        .getAvailableCategories(restrictions)
        .some(category => PathConstants.findRouteByCategory(category).value === pathname)
    )
  }

  const value = useActiveTab(pathname => {
    switch (pathname) {
      case HOME.value:
        return HOME.name
      case INSTITUTIONS.value:
        return INSTITUTIONS.name
      default:
        return findSelectedEnrollmentCategory(pathname)?.name ?? false
    }
  })

  if (!roles.hasBenefitMenu) return null

  const handleChange = (_: React.SyntheticEvent, newValue: string | boolean) => {
    const enrollmentCategory = enrollmentCategoryList.find(ec => ec.name === newValue)
    if (enrollmentCategory?.hasMultipleCategories) {
      AnalyticsTracker.trackEvent(AnalyticsEventName.HEADER_OPEN_OPTION_MENU, {
        option: newValue
      })
    } else {
      AnalyticsTracker.trackEvent(AnalyticsEventName.HEADER_NAVIGATE, {
        destination: newValue
      })
    }
  }

  const tabsSx = {
    minHeight: theme.spacing(8),
    height: theme.spacing(8)
  }

  const enrollmentCategoriesTabs = enrollmentCategoryList.map(ec => {
    return ec.hasMultipleCategories ? (
      <TabMenuApp
        key={ec.name}
        label={ec.title}
        value={ec.name}
        selectedValue={value}
        options={ec.categories.map(c => {
          return {
            label: c.title,
            to: PathConstants.findRouteByCategory(c).value,
            value: c.name
          }
        })}
        sx={tabsSx}
      />
    ) : (
      <TabApp
        key={ec.name}
        component={Link}
        label={ec.title}
        to={PathConstants.getCategoryRoutePath(ec.categories[0])}
        value={ec.name}
        selectedValue={value}
        sx={tabsSx}
      />
    )
  })

  return (
    <Container maxWidth="lg" disableGutters>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="standard"
          allowScrollButtonsMobile
          aria-label="scrollable force tabs example"
          sx={{
            height: theme.spacing('auto')
          }}
        >
          {[
            /* path: '/' | page: 'Home' */
            <TabApp
              key={HOME.name}
              component={Link}
              icon={<HomeIcon fontSize="small" />}
              to={HOME.value}
              value={HOME.name}
              selectedValue={value}
              aria-label={HOME.title}
              sx={{
                ...tabsSx,
                '.MuiTab-iconWrapper': {
                  marginBottom: 0
                }
              }}
            />,
            /* paths vary by enrollment category (e.g. /courses, /mentoring) | pages: dynamic from company content restrictions */
            ...enrollmentCategoriesTabs,
            /* path: '/partners' | page: 'Instituições' */
            <TabApp
              key={INSTITUTIONS.name}
              component={Link}
              label={INSTITUTIONS.title}
              to={INSTITUTIONS.value}
              value={INSTITUTIONS.name}
              selectedValue={value}
              aria-label={INSTITUTIONS.title}
              sx={tabsSx}
            />
          ]}
        </Tabs>

        <Box>{isProfileAssessmentEnabled && <ProfileAssessmentButton mode="desktop" />}</Box>
      </Box>
    </Container>
  )
}
